import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const DEFAULTS = {
  y: 32, // slide-up distance in px; set to 0 for an opacity-only fade
  opacity: 0,
  duration: 0.7,
  ease: 'power3.out',
  stagger: 0.12,
  start: 'top 85%',
}

/**
 * Fade (and optionally slide up) elements into view on scroll, the first time
 * each enters. Elements that cross the trigger together — a row of cards, say —
 * are batched so they stagger as a group; ones far apart animate on their own as
 * they arrive.
 *
 * Pass `y: 0` for an opacity-only fade. That matters for Swiper slides: writing a
 * transform on a slide would clobber Swiper's own translate, so those must never
 * be given a `y`.
 *
 * Respects `prefers-reduced-motion`: elements are simply shown, no movement.
 *
 * @param {string|Element|Element[]|NodeList} targets - elements (or a selector)
 * @param {object} [options] - overrides: { y, opacity, duration, ease, stagger, start }
 */
export function createScrollFade(targets, options = {}) {
  const config = { ...DEFAULTS, ...options }
  const elements = gsap.utils.toArray(targets)
  if (!elements.length) return

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reduceMotion) {
    gsap.set(elements, config.y ? { opacity: 1, y: 0 } : { opacity: 1 })
    return
  }

  const hidden = { opacity: config.opacity }
  if (config.y) hidden.y = config.y
  gsap.set(elements, hidden)

  ScrollTrigger.batch(elements, {
    start: config.start,
    once: true,
    onEnter: (batch) => {
      const vars = {
        opacity: 1,
        duration: config.duration,
        ease: config.ease,
        stagger: config.stagger,
        overwrite: 'auto',
      }
      if (config.y) vars.y = 0
      gsap.to(batch, vars)
    },
  })

  // The split-heading reveals re-wrap once fonts load, shifting layout; refresh
  // so these triggers measure against the final positions.
  if (document.fonts) document.fonts.ready.then(() => ScrollTrigger.refresh())
}
